/**
 * /el/guides/checklist-gamou
 *
 * The Greek counterpart of `wedding-planning-checklist.en.js` — the hub of the
 * Greek planning cluster, and the article the other three hang off.
 *
 * ── The differentiator, which is the whole piece ─────────────────────────
 * The Greek search landscape has the same problem the English one has: it is
 * full of month-by-month lists with a hundred items, and one more would be
 * worth nothing to a reader. The argument here is DEPENDENCY rather than
 * calendar:
 *
 *   περιορισμοί (προϋπολογισμός, αριθμός, ημερομηνία, χώρος)
 *     → τι επιτρέπουν (συνεργάτες, φαγητό, αισθητική)
 *       → τι επιτρέπουν οι επιβεβαιωμένοι (αριθμός, ποσότητες, τραπέζια)
 *         → λειτουργικά (επιβεβαιώσεις, πληρωμές, πρόγραμμα, παράδοση)
 *
 * Two sections carry it explicitly and every phase is written so its tasks are
 * visibly downstream of the phase before. An edit that turns a phase into a
 * flat list of chores removes the only reason this article exists.
 *
 * ── Greek rather than translated ─────────────────────────────────────────
 *  - **Η εποχή.** The English article says lead times are local. In Greece they
 *    are seasonal in a very specific way — May to September, and Saturdays —
 *    and that is worth saying, because it is the single biggest constraint on a
 *    Greek couple's date.
 *  - **Τα χαρτιά.** The English guide refuses to give legal instruction because
 *    jurisdiction varies. Here the jurisdiction is known, so the article names
 *    WHERE the answer lives — ο δήμος, η ενορία — and still states no document
 *    list and no deadline of its own. Naming an authority is not inventing a
 *    requirement; listing paperwork would be.
 *  - **Τα δικά μας τελευταία πράγματα.** Στέφανα, λαμπάδες, μπομπονιέρες and
 *    the κουμπάροι belong in the final-weeks list of a Greek wedding and have
 *    no English equivalent to translate from.
 *  - **Τα φιλοδωρήματα** keep the English hedge exactly: it varies by contract
 *    and by supplier, so ask rather than assume.
 *
 * ── The hedges, which are load-bearing ───────────────────────────────────
 * Nothing here may become a rule stated as fact: no absolute booking windows
 * (the article gives the ORDER — hardest to replace first — and tells the
 * reader to ask each supplier), no budget percentages, no formula producing a
 * guest count, no legal or paperwork instruction, no prescriptive claim about
 * tipping, and no payment or return deadlines of our invention.
 *
 * Banned and absent: «χωρίς άγχος», «τέλειος γάμος», «εγγυημένα», «δεν θα σας
 * ξεφύγει τίποτα», «ο απόλυτος οδηγός», and any promise that a timeline
 * produces an outcome.
 *
 * ── Boundaries with the guides it links to ───────────────────────────────
 * Four handoffs, none of them re-explained: the guest list, the RSVP deadline,
 * tracking the replies, and the tables. The invitation-timing section is
 * deliberately short — it states the chain and stops, because the numbers are
 * the point of the guide it hands to.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * All in the closing section, all already published on /el/wedding-planning:
 *
 *   - «Ο χώρος οργάνωσης και ο Ἔρως περιλαμβάνονται στο Heirloom.»
 *   - planning is divided into categories, each holding its own vendors, tasks,
 *     costs, documents and open decisions
 *   - dated things appear on a calendar, and a timeline shows the journey in
 *     order, some steps updating themselves
 *   - Éros reads what is in the space, answers from it, and makes a change when
 *     asked
 *
 * And what it must never say: that Agapaé plans the wedding, decides anything
 * on its own, finds, contacts or books suppliers, or replaces a planner. Éros
 * suggests and the couple decides — stated, not implied.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const weddingPlanningChecklistEl = {
  slug: "checklist-gamou",

  title: "Checklist γάμου: τι να οργανώσετε και πότε",
  metaTitle: "Checklist γάμου: τι να οργανώσετε και πότε | Agapaé",
  metaDescription:
    "Πρακτικό checklist γάμου με τη σειρά που έχει σημασία: τι αποφασίζεται πρώτο, ποιοι συνεργάτες κλείνονται νωρίς και τι μένει για τις τελευταίες εβδομάδες.",

  eyebrow: "Οργάνωση γάμου",

  hero: {
    src: "/assets/images/moments/moment_2.jpg",
    alt: "Απαλή ασπρόμαυρη φωτογραφία νύφης και γαμπρού που προχωρούν χέρι χέρι μέσα σε δυνατό φως, με τον γαμπρό σε σκούρο σμόκιν με παπιγιόν και λουλούδι στο πέτο και τη νύφη σε νυφικό με τετράγωνη λαιμόκοψη να κρατά μπουκέτο",
    width: 6558,
    height: 3659,
  },

  intro: [
    `Ο γάμος μοιάζει με μια ατελείωτη λίστα, όπου όλα δείχνουν επείγοντα ταυτόχρονα. Ελάχιστα είναι.`,
    `Τα περισσότερα περιμένουν μια απόφαση που δεν έχει παρθεί ακόμη. Το catering δεν σας δίνει τιμή χωρίς αριθμό ατόμων. Τα τραπέζια δεν σχεδιάζονται πριν έρθουν οι απαντήσεις.`,
    `Η σειρά που βοηθά, λοιπόν, δεν είναι η σειρά με την οποία σας έρχονται στο μυαλό. Είναι: προϋπολογισμός και αριθμός, μετά ημερομηνία και χώρος, μετά οι συνεργάτες που δύσκολα αντικαθίστανται, μετά τα προσκλητήρια και οι απαντήσεις, μετά οι λεπτομέρειες, μετά τα τραπέζια και οι τελικοί αριθμοί, και στο τέλος τα πρακτικά της εβδομάδας του γάμου.`,
    `Αυτό το checklist ακολουθεί αυτή τη σειρά και όχι ένα ημερολόγιο. Οι μήνες παρακάτω είναι κατά προσέγγιση· η σειρά είναι που κάνει τη δουλειά.`,
  ],

  sections: [
    {
      heading: "Το checklist με μια ματιά",
      content: [
        `Τα περισσότερα χρονοδιαγράμματα γάμου στήνονται γύρω στους δώδεκα μήνες. Είναι χρήσιμο πλαίσιο και όχι κανόνας: γάμοι οργανώνονται εξαιρετικά σε δεκαοκτώ μήνες και εξαιρετικά σε οκτώ εβδομάδες.`,
        `Χονδρικά, η δουλειά πέφτει σε επτά περιόδους:`,
        {
          type: "list",
          items: [
            `12 μήνες ή περισσότερο: προϋπολογισμός, αριθμός καλεσμένων, ημερομηνία, περιοχή και χώρος`,
            `περίπου 9 έως 6 μήνες: οι βασικοί συνεργάτες και η αισθητική που θα ακολουθήσουν`,
            `περίπου 6 έως 3 μήνες: προσκλητήρια, εμφάνιση και όσα αφορούν τους καλεσμένους`,
            `περίπου 3 έως 1 μήνα: απαντήσεις, τραπέζια και τα πρώτα πραγματικά πρακτικά`,
            `οι τελευταίες εβδομάδες: επιβεβαιώσεις, πληρωμές, πρόγραμμα και παραδόσεις`,
            `η εβδομάδα του γάμου: εκτέλεση, όχι νέες αποφάσεις`,
            `μετά τον γάμο: κλείσιμο όσων έμειναν`,
          ],
        },
        `Κάθε περίοδος εξαρτάται από την προηγούμενη. Γι' αυτό και η σειρά αντέχει σε έναν σύντομο αρραβώνα, ενώ ένα σταθερό ημερολόγιο όχι.`,
      ],
    },
    {
      heading: "12 μήνες ή περισσότερο: τα θεμέλια",
      content: [
        `Οι πρώτες αποφάσεις περιορίζουν όλες τις επόμενες, οπότε αξίζουν χρόνο.`,
        {
          type: "list",
          items: [
            `συμφωνήστε τι μπορείτε να ξοδέψετε και ποιος συνεισφέρει`,
            `υπολογίστε τον αριθμό των καλεσμένων και φτιάξτε ένα πρόχειρο της λίστας`,
            `αποφασίστε ημερομηνία, ή ένα εύρος ημερομηνιών, και την περιοχή`,
            `δείτε χώρους και κλείστε αυτόν που θέλετε`,
            `αποφασίστε αν θέλετε wedding planner, ή έστω κάποιον για τον συντονισμό της ημέρας`,
          ],
        },
        `Ο προϋπολογισμός, ο αριθμός και ο χώρος είναι μία απόφαση σε τρία κομμάτια: ο αριθμός που θέλετε μπορεί να μη χωράει στον χώρο που αγαπήσατε, και ο χώρος που αντέχετε εξαρτάται από το πόσοι θα είναι μέσα. Περιμένετε να πηγαινοέρχεστε ανάμεσά τους μέχρι να συμφωνήσουν και τα τρία.`,
        `Δεν υπάρχει σωστός προϋπολογισμός, ούτε ποσοστιαία κατανομή που να επιβιώνει σε έναν αληθινό γάμο.`,
        {
          type: "p",
          text: [
            `Η λίστα καλεσμένων είναι η πληροφορία που διαβάζουν σχεδόν όλες οι επόμενες αποφάσεις, οπότε αξίζει να χτιστεί ως αρχείο και όχι ως σελίδα με ονόματα. Το `,
            {
              text: "Λίστα καλεσμένων γάμου: πώς να την οργανώσετε",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` καλύπτει τα στρώματα που χρειάζεται.`,
          ],
        },
      ],
    },
    {
      heading: "9 έως 6 μήνες: κλείστε όσους δύσκολα αντικαθίστανται",
      content: [
        `Μόλις κλειδώσουν ημερομηνία και χώρος, οι συνεργάτες γίνονται κλεισίματα — οι περισσότεροι δεν κρατούν ημερομηνία χωρίς και τα δύο.`,
        `Αυτά που συνήθως προηγούνται:`,
        {
          type: "list",
          items: [
            `φωτογράφος και βιντεολήπτης`,
            `catering, όπου δεν το δίνει ο ίδιος ο χώρος`,
            `μουσική, είτε ορχήστρα είτε DJ είτε και τα δύο`,
            `ανθοστολισμός και διακόσμηση, μαζί με την αισθητική που θα ακολουθήσουν`,
            `η εκκλησία και ο ιερέας, ή ο δήμος για πολιτικό γάμο`,
            `wedding planner ή συντονιστής, αν δεν τον κλείσατε νωρίτερα`,
          ],
        },
        `Ο κανόνας που αξίζει να κρατήσετε δεν είναι ο μήνας. Είναι αυτός: κλείστε πρώτους όσους είναι ταυτόχρονα σημαντικοί για εσάς και δύσκολο να αντικατασταθούν.`,
        `Ποιοι είναι αυτοί εξαρτάται και από την εποχή. Στην Ελλάδα η αιχμή είναι το καλοκαίρι, και τα Σάββατα από τον Μάιο ως τον Σεπτέμβριο κλείνονται πολύ νωρίς — χώροι και φωτογράφοι συχνά έναν χρόνο πριν, ενώ άλλοι συνεργάτες βρίσκονται και με λιγότερο χρόνο.`,
        `Ρωτήστε τον καθένα πόσο μπροστά κλείνει, και αφήστε τις απαντήσεις να ορίσουν τη σειρά σας.`,
      ],
    },
    {
      heading: "6 έως 3 μήνες: η εμπειρία των καλεσμένων",
      content: [
        `Η μεσαία περίοδος είναι εκεί όπου ο γάμος γίνεται κάτι που θα ζήσουν οι καλεσμένοι, και όπου βρίσκονται οι περισσότερες ορατές αποφάσεις.`,
        {
          type: "list",
          items: [
            `το προσκλητήριο, και μια σελίδα με πληροφορίες αν σκοπεύετε να φτιάξετε`,
            `νυφικό, κοστούμι, πρόβες και βέρες`,
            `το μενού, μια δοκιμή όπου προσφέρεται, και η τούρτα`,
            `λουλούδια και διακόσμηση στη λεπτομέρειά τους`,
            `μετακινήσεις, και πληροφορίες διαμονής για όσους ταξιδεύουν`,
            `οι λεπτομέρειες του μυστηρίου και η μουσική κάθε στιγμής`,
            `το ταξίδι του μέλιτος, ή ό,τι ακολουθεί`,
          ],
        },
        `Κανένας γάμος δεν χρειάζεται όλα αυτά. Σημασία έχει ο χαρακτήρας της δουλειάς: αυτές οι αποφάσεις παίρνονται με άνεση, γιατί τίποτα άλλο δεν τις περιμένει. Λίγους μήνες αργότερα αυτό παύει να ισχύει.`,
      ],
    },
    {
      heading: "Πότε φεύγουν τα προσκλητήρια",
      content: [
        `Μια μικρή απόφαση με μακρύ αποτύπωμα. Το πότε φεύγουν τα προσκλητήρια ορίζει πόσο χρόνο έχουν οι καλεσμένοι να απαντήσουν, που ορίζει την προθεσμία σας, που ορίζει πότε μπορείτε να δώσετε τελικό αριθμό σε οποιονδήποτε.`,
        `Πόσο περιθώριο χρειάζεστε εξαρτάται από πράγματα που αφορούν τον δικό σας γάμο:`,
        {
          type: "list",
          items: [
            `αν οι καλεσμένοι ταξιδεύουν, και αν ο γάμος γίνεται σε νησί ή στο εξωτερικό`,
            `αν στείλατε save the date, που σας γυρίζει πίσω λίγο χρόνο`,
            `τον χρόνο παραγωγής και παράδοσης για ό,τι τυπώνεται`,
            `το αν θα δοθούν στο χέρι, που θέλει ραντεβού και εβδομάδες`,
            `πότε ζητούν αριθμό το catering ή ο χώρος`,
          ],
        },
        {
          type: "p",
          text: [
            `Το να μετρήσετε αυτά ανάποδα από την ημερομηνία του γάμου είναι θέμα από μόνο του, και το `,
            {
              text: "Προθεσμία RSVP γάμου: πότε να ζητήσετε τις απαντήσεις",
              href: "/guides/when-should-wedding-guests-rsvp",
            },
            ` το αναλύει.`,
          ],
        },
      ],
    },
    {
      heading: "3 έως 1 μήνα: από τα σχέδια στους αριθμούς",
      content: [
        `Εδώ αλλάζει η ταχύτητα: από εδώ και πέρα η οργάνωση είναι ονόματα, αριθμοί και ώρες, όχι αποφάσεις.`,
        {
          type: "list",
          items: [
            `παρακολουθήστε τις απαντήσεις πάνω στη λίστα που στείλατε`,
            `επικοινωνήστε προσωπικά με όσους δεν έχουν απαντήσει`,
            `επιβεβαιώστε τι χρειάζεται το catering και μέχρι πότε`,
            `ξεκινήστε το πλάνο τραπεζιών με τις απαντήσεις που έχετε`,
            `κλειδώστε τις λεπτομέρειες μυστηρίου και δεξίωσης, τις μετακινήσεις και την πρόσβαση στον χώρο`,
            `ετοιμάστε πινακίδες, μενού και καρτελάκια μόλις σταθεροποιηθούν οι αριθμοί`,
            `κάντε τις τελευταίες πρόβες`,
          ],
        },
        {
          type: "p",
          text: [
            `Οι απαντήσεις έρχονται με κάθε δυνατό τρόπο, και το να μένουν καθαρές είναι το μεγαλύτερο μέρος της δουλειάς σε αυτή τη φάση. Το `,
            {
              text: "Πώς να οργανώσετε τα RSVP του γάμου χωρίς να χάσετε καλεσμένους",
              href: "/guides/how-to-organize-wedding-rsvps",
            },
            ` καλύπτει αυτό το κομμάτι.`,
          ],
        },
        `Ξεκινήστε το πλάνο τραπεζιών πριν έρθουν όλες οι απαντήσεις: ένα πλάνο χτισμένο πάνω στις περισσότερες απορροφά τις υπόλοιπες, ενώ ένα που ξεκινά μετά την τελευταία απάντηση χτίζεται μέσα σε πίεση.`,
      ],
    },
    {
      heading: "Γιατί η λίστα προηγείται των τραπεζιών",
      content: [
        `Το πιο χρήσιμο πράγμα που μπορείτε να καταλάβετε για την οργάνωση ενός γάμου είναι ποια απόφαση ξεκλειδώνει ποια.`,
        {
          type: "steps",
          items: [
            `Η λίστα αποφασίζει ποιοι καλούνται.`,
            `Τα προσκλητήρια φέρνουν την απόφαση μπροστά στους ανθρώπους.`,
            `Οι απαντήσεις καταγράφουν τι είπαν.`,
            `Οι επιβεβαιωμένοι γίνονται ο τελικός αριθμός.`,
            `Ο αριθμός ορίζει ποσότητες φαγητού και πόσα τραπέζια χρειάζεστε.`,
            `Το πλάνο μοιράζει τους ανθρώπους σε αυτά τα τραπέζια.`,
          ],
        },
        `Κάθε βήμα καταναλώνει το προηγούμενο. Τραπέζια σχεδιασμένα πριν από τις απαντήσεις σχεδιάζονται δύο φορές, και ένας αριθμός βγαλμένος από λίστα που δεν έλεγξε κανείς είναι αριθμός που δεν μπορείτε να υπερασπιστείτε.`,
        {
          type: "p",
          text: [
            `Το τελευταίο βήμα είναι δουλειά από μόνο του, και το `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            ` πιάνει την αλυσίδα από τη στιγμή που έχετε τις απαντήσεις σας.`,
          ],
        },
      ],
    },
    {
      heading: "Έναν μήνα πριν: σταματήστε να αποφασίζετε",
      content: [
        `Τον τελευταίο μήνα, το πιο πολύτιμο πράγμα που μπορείτε να κάνετε είναι να σταματήσετε να έχετε νέες ιδέες. Σχεδόν όλα από εδώ και πέρα είναι επιβεβαίωση και όχι δημιουργία:`,
        {
          type: "list",
          items: [
            `δώστε τους τελικούς αριθμούς στην ημερομηνία που ζητά ο καθένας`,
            `επιβεβαιώστε ώρες άφιξης και στησίματος με όσους δουλεύουν την ημέρα`,
            `κλειδώστε το πλάνο τραπεζιών και ό,τι τυπώνεται από αυτό`,
            `επιβεβαιώστε μετακινήσεις και τη διαμονή που κανονίσατε για καλεσμένους`,
            `δείτε τι οφείλεται ακόμη και πότε πληρώνεται`,
            `κλειδώστε το πρόγραμμα της ημέρας και το ποιος έχει την ευθύνη για τι`,
          ],
        },
        `Μια προσθήκη της τελευταίας στιγμής σπάνια μένει μόνη της: μια νέα ιδέα τον τελευταίο μήνα έρχεται συνήθως μαζί με μια πληρωμή, μια παράδοση, μια αλλαγή στο πρόγραμμα και αρκετούς ανθρώπους που πρέπει να ενημερωθούν.`,
        {
          type: "note",
          text: `Τα χαρτιά του γάμου — η άδεια από τον δήμο, και ό,τι ζητά η ενορία σας για θρησκευτικό γάμο — έχουν δικές τους προθεσμίες και δικά τους δικαιολογητικά. Ρωτήστε νωρίς τι ακριβώς ισχύει στη δική σας περίπτωση, ώστε να μη γίνει ποτέ πρόβλημα του τελευταίου μήνα.`,
        },
      ],
    },
    {
      heading: "Η τελευταία εβδομάδα: παραδώστε τις λεπτομέρειες",
      content: [
        `Η τελευταία εβδομάδα είναι παράδοση, όχι σπριντ. Ό,τι υπάρχει μόνο μέσα στο κεφάλι σας πρέπει να βγει από εκεί: την ημέρα του γάμου δεν πρέπει να είστε εσείς αυτοί που απαντούν πού πάνε τα λουλούδια.`,
        `Μια παράδοση περιέχει συνήθως:`,
        {
          type: "list",
          items: [
            `το πρόγραμμα της ημέρας, με τηλέφωνα συνεργατών και ώρες άφιξης`,
            `την πρόσβαση στον χώρο, το στήσιμο και το μάζεμα`,
            `τι μένει να πληρωθεί, σε ποιον και πώς, σύμφωνα με τα συμφωνητικά σας`,
            `ό,τι πρέπει να φτάσει στον χώρο και ποιος το μεταφέρει`,
            `τα προσωπικά σας, τα στέφανα, τις βέρες και τα χαρτιά`,
          ],
        },
        `Πείτε καθαρά ποιος είναι αυτός ο άνθρωπος — ένας συντονιστής, ο κουμπάρος, ένας συγγενής που τα πάει καλά με τις λίστες — και πείτε το και στους συνεργάτες σας, ώστε οι ερωτήσεις να φτάνουν σε κάποιον που μπορεί να απαντήσει ενώ εσείς ετοιμάζεστε.`,
        `Για τα φιλοδωρήματα, το τι συνηθίζεται διαφέρει ανά συνεργάτη και ανά συμφωνία. Δείτε τι λένε τα συμφωνητικά σας, ρωτήστε αν δεν είστε σίγουροι, και ετοιμάστε τα από πριν και όχι το πρωί.`,
      ],
    },
    {
      heading: "Την ημέρα: ακολουθήστε το πλάνο",
      content: [
        `Η ημέρα του γάμου είναι για εκτέλεση. Μέχρι τότε, κάποιος πρέπει να κρατά:`,
        {
          type: "list",
          items: [
            `το τελικό πρόγραμμα και τις συμφωνημένες ευθύνες`,
            `τα τηλέφωνα των συνεργατών, και κάποιον να πάρει για ό,τι προκύψει`,
            `τα προσωπικά αντικείμενα και ό,τι μεταφέρεται`,
            `τα χαρτιά που χρειάζεται το μυστήριο`,
          ],
        },
        `Κάτι θα πάει λίγο αλλιώς· σχεδόν πάντα πάει, και σχεδόν τίποτα από αυτά δεν το αντιλαμβάνεται κανείς άλλος. Η οργάνωση ήταν η δουλειά. Η μέρα είναι ο γάμος.`,
      ],
    },
    {
      heading: "Μετά τον γάμο: κλείστε τα ανοιχτά",
      content: [
        `Λίγα πράγματα ζουν περισσότερο από την ημέρα:`,
        {
          type: "list",
          items: [
            `τακτοποιήστε ό,τι μένει με τους συνεργάτες σας, σύμφωνα με τα συμφωνητικά`,
            `επιστρέψτε ό,τι νοικιάστηκε μέσα στις συμφωνημένες προθεσμίες`,
            `μαζέψτε ό,τι έμεινε στον χώρο και φυλάξτε τα χαρτιά του γάμου`,
            `συγκεντρώστε τις φωτογραφίες και τα βίντεο, από τον φωτογράφο και από τους καλεσμένους`,
            `ευχαριστήστε όσους ταξίδεψαν, βοήθησαν ή έδωσαν κάτι`,
            `κρατήστε τα στοιχεία των συνεργατών και βάλτε όσα αξίζουν κάπου που θα αντέξει`,
          ],
        },
        `Οι προθεσμίες εδώ ανήκουν στα δικά σας συμφωνητικά και στους συνεργάτες σας, όχι σε κάποιο checklist: τα διαστήματα επιστροφής, τα υπόλοιπα και το πόσο μένει διαθέσιμο ένα άλμπουμ τα ορίζει αυτός που τα παρέχει.`,
      ],
    },
    {
      heading: "Κι αν έχετε λιγότερο από έναν χρόνο;",
      content: [
        `Οι σύντομοι αρραβώνες δουλεύουν. Αυτό που δεν δουλεύει είναι να κρατήσετε το δωδεκάμηνο πλάνο και να σβήσετε όσα σας φαίνονται λιγότερο σημαντικά: συνήθως αυτά είναι που περίμεναν οι επόμενες αποφάσεις.`,
        `Συμπιέστε τη σειρά αντί να την κόψετε. Η σειρά μένει· τα διαστήματα μικραίνουν και επικαλύπτονται.`,
        { type: "subheading", text: "Αν έχετε γύρω στους έξι μήνες" },
        {
          type: "list",
          items: [
            `κλείστε προϋπολογισμό, αριθμό, ημερομηνία και χώρο μέσα στις πρώτες εβδομάδες`,
            `πλησιάστε αμέσως όσους δύσκολα αντικαθίστανται`,
            `μείνετε ευέλικτοι στην ημερομηνία και στη μέρα της εβδομάδας, γιατί η διαθεσιμότητα είναι ο πραγματικός περιορισμός`,
            `στείλτε save the date ή προσκλητήρια μόλις επιβεβαιωθεί ο χώρος`,
            `αφήστε έξω ό,τι προαιρετικό προσθέτει πολυπλοκότητα, ειδικά ό,τι θέλει χρόνο να παραχθεί`,
          ],
        },
        { type: "subheading", text: "Αν έχετε γύρω στους τρεις μήνες" },
        {
          type: "list",
          items: [
            `ρωτήστε ποιος είναι διαθέσιμος πριν αποφασίσετε τι θέλετε`,
            `καλέστε γρήγορα, με καθαρή και πρώιμη προθεσμία απάντησης`,
            `κρατήστε τα βασικά: έναν χώρο, φαγητό, κάποιον να φωτογραφίσει, κάποιον να τελέσει τον γάμο, μουσική`,
            `κρατήστε την ίδια σειρά εξαρτήσεων, με μέρες ανάμεσα στα βήματα αντί για μήνες`,
          ],
        },
        `Ένα μεγαλύτερο χρονοδιάγραμμα δεν κάνει τον γάμο καλύτερο, ούτε ένα μικρότερο χειρότερο. Αυτό που αφαιρεί είναι ο χώρος να ξανασκεφτείτε αποφάσεις — γι' αυτό η σειρά μετράει περισσότερο, όχι λιγότερο.`,
      ],
    },
    {
      heading: "Ένας καλύτερος τρόπος να δείτε το checklist",
      content: [
        `Ένα checklist γάμου δεν είναι εκατό πράγματα να τσεκάρετε. Είναι λίγες αποφάσεις, καθεμία από τις οποίες ξεκλειδώνει τις επόμενες. Δύο αλυσίδες κρατούν σχεδόν ολόκληρο τον γάμο.`,
        `Η αλυσίδα των καλεσμένων:`,
        {
          type: "steps",
          items: [
            `Ο αριθμός σας ορίζει το μέγεθος του γάμου.`,
            `Ο χώρος πρέπει να τον χωράει, και το φαγητό κοστολογείται πάνω σε αυτόν.`,
            `Τα προσκλητήρια γράφονται από τη λίστα που υπάρχει από πίσω.`,
            `Οι απαντήσεις μετατρέπουν τη λίστα σε επιβεβαιωμένους.`,
            `Ο τελικός αριθμός είναι αυτό που δίνετε στους συνεργάτες, και από αυτόν βγαίνουν τα τραπέζια.`,
          ],
        },
        `Η αλυσίδα της ημερομηνίας:`,
        {
          type: "steps",
          items: [
            `Η ημερομηνία και ο χώρος ορίζουν ποιοι συνεργάτες είναι διαθέσιμοι, και η διαθεσιμότητα ορίζει ποιους μπορείτε να κλείσετε.`,
            `Τα κλεισίματα ορίζουν το σχήμα της ημέρας, που γίνεται το πρόγραμμα.`,
            `Από το πρόγραμμα γράφονται οι πληροφορίες που δίνετε στους καλεσμένους.`,
          ],
        },
        `Διαβασμένο έτσι, το checklist παύει να είναι τεστ μνήμης. Η ερώτηση δεν είναι πόσα έμειναν, αλλά τι μπλοκάρει αυτή τη στιγμή κάτι άλλο — και αυτό είναι σχεδόν πάντα το επόμενο που πρέπει να κάνετε.`,
      ],
    },
    {
      heading: "Όλο το πλάνο σε ένα σημείο",
      content: [
        `Με ό,τι κι αν οργανώνετε — ένα τετράδιο, ένα αρχείο, ένα κοινό έγγραφο — ένας κανόνας μετράει περισσότερο από το εργαλείο: υπάρχει μία εκδοχή του πλάνου, και αυτή είναι η σωστή.`,
        {
          type: "note",
          text: `Ο χώρος οργάνωσης και ο Ἔρως στο Agapaé περιλαμβάνονται στο πακέτο Heirloom.`,
        },
        `Στο Agapaé η οργάνωση είναι χωρισμένη στα μέρη από τα οποία αποτελείται ένας γάμος — ο χώρος, το φαγητό, η φωτογράφιση, τα λουλούδια — και το καθένα κρατά τους δικούς του συνεργάτες, εργασίες, κόστη, έγγραφα και ανοιχτές αποφάσεις. Ό,τι έχει ημερομηνία εμφανίζεται σε ένα ημερολόγιο, και ένα χρονοδιάγραμμα δείχνει τη διαδρομή με τη σειρά, με κάποια βήματα να ενημερώνονται μόνα τους καθώς έρχονται οι απαντήσεις και δένουν τα τραπέζια.`,
        `Ο Ἔρως είναι ο βοηθός μέσα σε αυτόν τον χώρο: διαβάζει ό,τι υπάρχει ήδη εκεί, απαντά για τον δικό σας γάμο και κάνει μια αλλαγή όταν του το ζητήσετε. Προτείνει και αποφασίζετε εσείς — το Agapaé δεν διαλέγει συνεργάτες, δεν κλείνει κανέναν και δεν οργανώνει τον γάμο για λογαριασμό σας.`,
        {
          type: "p",
          text: [
            `Η οργάνωση περιγράφεται στη σελίδα `,
            { text: "Οργάνωση γάμου", href: "/wedding-planning" },
            `, όπου η λίστα, οι απαντήσεις και τα τραπέζια ζουν πάνω στο ίδιο πλάνο.`,
          ],
        },
      ],
    },
    {
      heading: "Το checklist, συνοπτικά",
      content: [
        `Όλο το κείμενο, συμπιεσμένο:`,
        { type: "subheading", text: "Θεμέλια" },
        {
          type: "list",
          items: [
            `συμφωνήστε προϋπολογισμό`,
            `υπολογίστε αριθμό και φτιάξτε πρόχειρη λίστα`,
            `διαλέξτε ημερομηνία και περιοχή, και κλείστε τον χώρο`,
          ],
        },
        { type: "subheading", text: "Βασικά κλεισίματα" },
        {
          type: "list",
          items: [
            `κλείστε όσους δύσκολα αντικαθίστανται`,
            `επιβεβαιώστε συμφωνητικά και προκαταβολές γραπτώς`,
            `αποφασίστε την αισθητική`,
          ],
        },
        { type: "subheading", text: "Οι καλεσμένοι" },
        {
          type: "list",
          items: [
            `αποφασίστε πώς και πότε καλείτε`,
            `ετοιμάστε πληροφορίες, εμφάνιση, μυστήριο και δεξίωση`,
          ],
        },
        { type: "subheading", text: "Διαχείριση" },
        {
          type: "list",
          items: [
            `στείλτε τα προσκλητήρια`,
            `κρατήστε τις απαντήσεις και κυνηγήστε όσες λείπουν`,
            `κλείστε τον τελικό αριθμό`,
          ],
        },
        { type: "subheading", text: "Τελική οργάνωση" },
        {
          type: "list",
          items: [
            `τελειώστε το πλάνο τραπεζιών και ό,τι τυπώνεται από αυτό`,
            `επιβεβαιώστε κάθε συνεργάτη, το πρόγραμμα και τα πρακτικά`,
          ],
        },
        { type: "subheading", text: "Η εβδομάδα του γάμου" },
        {
          type: "list",
          items: [
            `παραδώστε τις ευθύνες σε συγκεκριμένο άνθρωπο`,
            `μοιράστε τηλέφωνα και πρόγραμμα`,
            `ετοιμάστε προσωπικά αντικείμενα και χαρτιά`,
            `αφήστε το πλάνο να τρέξει`,
          ],
        },
        `Αν μπορείτε να πείτε τι μπλοκάρει αυτή τη στιγμή κάτι άλλο, είστε πιο μπροστά απ' όσο δείχνει το μήκος οποιασδήποτε λίστας.`,
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Πότε να ξεκινήσω να οργανώνω τον γάμο;`,
      a: `Μόλις έχετε στο μυαλό σας μια ημερομηνία, ή έστω μια εποχή. Πολλά ζευγάρια βρίσκουν άνετους τους δώδεκα με δεκαοκτώ μήνες, κυρίως επειδή χώροι και περιζήτητοι συνεργάτες κλείνονται τόσο νωρίς. Και τα πιο σύντομα χρονοδιαγράμματα δουλεύουν· αλλάζει η ταχύτητα, όχι η σειρά.`,
    },
    {
      q: `Τι κάνω πρώτο;`,
      a: `Προϋπολογισμό και αριθμό καλεσμένων, πριν κλείσετε οτιδήποτε. Μαζί ορίζουν ποιοι χώροι είναι ρεαλιστικοί, και ο χώρος ορίζει μετά την ημερομηνία και πολλά από όσα ακολουθούν. Φτιάξτε παράλληλα ένα πρόχειρο της λίστας.`,
    },
    {
      q: `Γίνεται να οργανώσω γάμο σε έξι μήνες;`,
      a: `Ναι, και γίνεται συχνά. Συμπιέστε τη σειρά αντί να πετάξετε βήματα στην τύχη: κλείστε γρήγορα προϋπολογισμό, αριθμό, ημερομηνία και χώρο, πλησιάστε αμέσως όσους δύσκολα αντικαθίστανται και μείνετε ευέλικτοι στη μέρα της εβδομάδας. Σε σύντομο χρονοδιάγραμμα, η διαθεσιμότητα είναι ο πραγματικός περιορισμός.`,
    },
    {
      q: `Πότε στέλνονται τα προσκλητήρια;`,
      a: `Αρκετά νωρίς ώστε να προλάβουν να απαντήσουν πριν χρειαστείτε τελικό αριθμό. Αυτό εξαρτάται από το αν ταξιδεύουν, από το αν στείλατε save the date, από το πώς θα απαντήσουν και από το πότε ζητά αριθμό το catering. Μετρήστε ανάποδα από τον γάμο.`,
    },
    {
      q: `Τι κάνω τον τελευταίο μήνα;`,
      a: `Επιβεβαιώνετε, δεν δημιουργείτε. Δώστε τελικούς αριθμούς στην ημερομηνία που ζητά ο καθένας, συμφωνήστε ώρες άφιξης και στησίματος, κλείστε το πλάνο τραπεζιών και ό,τι τυπώνεται, φτιάξτε το πρόγραμμα της ημέρας, δείτε τι οφείλεται και αποφασίστε ποιος έχει την ευθύνη για τι.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["how-to-create-wedding-budget", "how-to-track-wedding-vendors-payments"],

    product: [
      {
        href: "/wedding-planning",
        label: "Οργάνωση γάμου",
        blurb:
          "Δείτε πώς ο χώρος οργάνωσης κρατά συνεργάτες, εργασίες, προϋπολογισμό, έγγραφα και αποφάσεις σε ένα ιδιωτικό σημείο, με χρονοδιάγραμμα και ημερολόγιο από πάνω. Διαθέσιμο στο πακέτο Heirloom.",
      },
    ],
  },
};

export default weddingPlanningChecklistEl;
